import type { TableProps } from 'antd';
import { FileIcon } from '@dynamika-shared/ui/icons';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { shortDate } from '@dynamika-shared/utils/date';
import { twMerge } from '@dynamika-shared/utils/twMerge';
import { QCTReading } from '@dynamika-shared/types/QCTReading';
import { Label } from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { QCTScanTypeLabels, toAcquisitionDate } from 'src/utils/qctHelpers';

const EMPTY = '—';

const EmptyCell = () => (
  <div className='flex items-center h-full text-spanishGray'>{EMPTY}</div>
);

/**
 * The two tables these columns serve.
 *
 * Plain terms
 * -----------
 * The workstation table stages label assignments that have not been saved yet;
 * the readings table edits results the visit already holds. They share the
 * measurement columns and nothing else.
 *
 * Technical
 * ---------
 * A discriminated union rather than a bag of optionals: `mode` says which set
 * of callbacks is present, so the compiler rejects a workstation column built
 * with `onRemove`, and `canEditAssignedLabel` no longer has to be inferred
 * from "was a callback passed".
 */
type WorkstationColumnsProps = {
  mode: 'workstation';
  /** `null` clears the staged assignment for that row. */
  onSelect: (rowKey: string, labelId: string | null) => void;
  allLabels: Label[];
  availableLabels: Label[];
  selected: Record<string, string>;
  readOnly: boolean;
};

type ReadingsColumnsProps = {
  mode: 'readings';
  onRemove: (reading: QCTReading) => void;
  onLabelChange: (reading: QCTReading, labelId: string) => void;
  availableLabels: Label[];
  readOnly: boolean;
};

type MakeColumnsProps = WorkstationColumnsProps | ReadingsColumnsProps;

export function makeColumns(
  props: MakeColumnsProps
): TableProps<QCTReading>['columns'] {
  // Narrowed once here: TypeScript cannot carry a `props.mode` check into the
  // render callbacks below, which are closures.
  const workstation = props.mode === 'workstation' ? props : null;
  const readings = props.mode === 'readings' ? props : null;

  const options = props.availableLabels.map(label => ({
    label: label.name,
    value: label.id,
  }));

  /**
   * Whether the assigned label is a control or just text.
   *
   * Plain terms
   * -----------
   * Only a result that is already saved for reading can have its label changed
   * or taken off, and only while the task is still open. Everywhere else the
   * label is shown as plain text.
   *
   * Technical
   * ---------
   * Only the readings table edits a saved label — the workstation table stages
   * its assignments locally and commits them through `onSelect`. The flag also
   * drives the column width, which a select needs and a text cell does not.
   */
  const canEditAssignedLabel = !!readings && !readings.readOnly;

  /**
   * Labels a saved result may be moved to.
   *
   * Plain terms
   * -----------
   * The labels nobody has taken yet, plus the one this result already carries —
   * otherwise the specialist would open the list and not find the value the row
   * is currently set to.
   *
   * Technical
   * ---------
   * `availableLabels` arrives as `filterQCTLabels(groupedLabels, visitData)`:
   * every study QCT label MINUS the ones assigned anywhere in the visit, which
   * excludes this row's own. Re-adding just that one keeps the 1 label ↔ 1
   * result rule (D7-2775) while leaving the current value selectable.
   */
  const optionsForSavedRow = (row: QCTReading) => {
    const own =
      row.labelId && row.labelName
        ? [{ label: row.labelName, value: row.labelId }]
        : [];

    return [...own, ...options];
  };

  return [
    {
      key: 'examDate',
      dataIndex: 'examDate',
      // On the workstation table the acquisition date is already the group
      // heading, so repeating it in every row would be noise.
      hidden: !!workstation,
      title: 'Acquisition Date',
      render(text, row) {
        // A calendar date, rendered as reported. `toAcquisitionDate` is what
        // keeps it off the viewer's timezone — see its doc comment.
        const acquisitionDate = toAcquisitionDate(row.examDate);

        if (!acquisitionDate) {
          return <EmptyCell />;
        }

        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-semibold'>
            {shortDate(acquisitionDate)}
          </div>
        );
      },
    },
    {
      key: 'scanType',
      dataIndex: 'scanType',
      title: 'Region',
      render(text, row) {
        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            {(row.scanType && QCTScanTypeLabels[row.scanType]) ?? EMPTY}
          </div>
        );
      },
    },
    {
      key: 'qctData',
      title: 'QCT Data',
      render() {
        /**
         * The cell names the KIND of file, not the file.
         *
         * Plain terms
         * -----------
         * Every result the workstation returns is QCT data held as JSON, so
         * every row says so. What the workstation stored as the file's name is
         * the patient's own code — it tells the specialist nothing they cannot
         * already see, and repeats one identifier down the whole column. A
         * result the workstation left unnamed is no less JSON than the rest,
         * so it reads the same.
         *
         * Technical
         * ---------
         * Renders from nothing: the row is not consulted, because `fileName`
         * no longer decides what is shown. The value is still parsed, still
         * stored on the visit and still matched by the workstation search — it
         * is the display that drops, not the field (D7-2207). It is what the
         * planned (i) hint will show.
         */
        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            <FileIcon className='h-3 w-3 mr-1 shrink-0 text-pastelBlue' />
            <span>JSON DATA</span>
          </div>
        );
      },
    },
    {
      key: 'labelName',
      dataIndex: 'labelName',
      // Workstation rows carry no saved label — `toWorkstationReadings` leaves
      // `labelName` unset — so this column rendered an em-dash in every row of
      // that table, right beside the select that does the assigning.
      hidden: !!workstation,
      width: canEditAssignedLabel ? 200 : undefined,
      title: 'Assigned label',
      render(text, row) {
        if (canEditAssignedLabel) {
          return (
            <div className='mt-1.5 text-start'>
              <Select
                data-testid={`QCTChangeLabel-${row.rowKey}`}
                placeholder='Assign Label'
                className='w-full !h-[4rem]'
                value={
                  row.labelId
                    ? { label: row.labelName, value: row.labelId }
                    : undefined
                }
                onChange={value => readings?.onLabelChange(row, value)}
                options={optionsForSavedRow(row)}
              />
            </div>
          );
        }

        if (!row.labelName) {
          return <EmptyCell />;
        }

        return (
          <div className='flex items-center h-full text-1624 mini:text-1216 font-medium'>
            {row.labelName}
          </div>
        );
      },
    },
    {
      hidden: !!workstation,
      key: 'action',
      title: 'Action',
      align: 'right',
      render(text, row) {
        return (
          <div className='flex items-center h-full justify-end font-semibold'>
            <button
              data-testid={`QCTRemoveFromReading-${row.rowKey}`}
              onClick={() => readings?.onRemove(row)}
              className={twMerge(
                'text-vividTangelo hover:text-dodgerBlue',
                props.readOnly &&
                  'text-spanishGray hover:text-spanishGray cursor-not-allowed'
              )}
              disabled={props.readOnly}
            >
              Remove
            </button>
          </div>
        );
      },
    },
    {
      hidden: !workstation,
      key: 'assignLabel',
      title: 'Assign Label',
      width: 200,
      align: 'right',
      render(text, row) {
        // Keyed by `rowKey`, not `referenceId`: one record can describe several
        // scans and each is its own row with its own label.
        const { rowKey } = row;

        const selectedLabelId = workstation?.selected[rowKey];

        const selectedLabel = workstation?.allLabels.find(
          label => label.id === selectedLabelId
        );

        return (
          <div className='mt-1.5 text-start'>
            <Select
              allowClear
              data-testid={`QCTAssignLabel-${rowKey}`}
              placeholder='Assign Label'
              className='w-full !h-[4rem]'
              value={
                selectedLabelId
                  ? { label: selectedLabel?.name, value: selectedLabelId }
                  : undefined
              }
              // antd reports a cleared select as `undefined`; that is a
              // removal, and `onSelect` is told so explicitly rather than
              // being handed the current label to toggle off.
              onChange={value => workstation?.onSelect(rowKey, value ?? null)}
              options={options}
              disabled={props.readOnly}
            />
          </div>
        );
      },
    },
  ];
}
